<template>
  <div id="modulos" class="product-modules-area ptb-100">
    <div class="container">
      <div class="section-title text-center">
        <span class="sub-title">Funcionalidades</span>
        <h2>Tudo que o {{ product.name }} oferece</h2>
        <p>Clique em cada módulo para conhecer os detalhes. Complementos podem ser ativados conforme a necessidade.</p>
      </div>

      <div class="categories-tabs" data-aos="fade-up">
        <button
          v-for="category in product.categories"
          :key="category.id"
          :class="['category-tab', { active: activeCategory === category.id }]"
          @click="activeCategory = category.id"
        >
          <i :class="category.icon"></i>
          {{ category.name }}
        </button>
      </div>

      <div class="modules-grid" data-aos="fade-up" data-aos-delay="100">
        <div
          v-for="module in filteredModules"
          :key="module.id"
          class="module-card"
          @click="openModal(module)"
        >
          <div class="module-icon">
            <i :class="module.icon"></i>
          </div>
          <h3>{{ module.name }}</h3>
          <p>{{ module.shortDesc }}</p>
          <div class="module-badge" v-if="module.badge">
            {{ module.badge }}
          </div>
        </div>
      </div>

      <div class="demo-cta" data-aos="fade-up">
        <div class="demo-content">
          <h3>Quer ver o {{ product.name }} em ação?</h3>
          <p>Agende uma demonstração personalizada com nossa equipe e conheça os módulos na prática.</p>
          <a
            :href="demoUrl"
            target="_blank"
            class="btn-style-one green-color"
            rel="noopener noreferrer"
          >
            Solicitar Demonstração
            <i class="ph-caret-right"></i>
          </a>
        </div>
      </div>
    </div>

    <transition name="modal-fade">
      <div v-if="showModal" class="modal-overlay" @click="closeModal">
        <div class="modal-container" @click.stop>
          <button class="modal-close" @click="closeModal">
            <i class="ph-x"></i>
          </button>

          <div class="modal-header">
            <div class="modal-icon">
              <i :class="selectedModule.icon"></i>
            </div>
            <h2>{{ selectedModule.name }}</h2>
            <p class="modal-category">{{ getCategoryName(selectedModule.category) }}</p>
          </div>

          <div class="modal-body">
            <p class="module-description">{{ selectedModule.fullDesc }}</p>

            <h3>Principais Funcionalidades:</h3>
            <ul class="features-list">
              <li v-for="(feature, index) in selectedModule.features" :key="index">
                <i class="ph-check-circle"></i>
                {{ feature }}
              </li>
            </ul>

            <div class="modal-footer">
              <a
                :href="moduleDemoUrl"
                target="_blank"
                class="btn-style-one green-color"
                rel="noopener noreferrer"
              >
                Solicitar Demonstração
              </a>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'ProductModules',
  props: {
    product: {
      type: Object,
      required: true,
    },
  },
  data() {
    const firstCategory = this.product.categories && this.product.categories[0]
    return {
      activeCategory: firstCategory ? firstCategory.id : '',
      showModal: false,
      selectedModule: {},
    }
  },
  computed: {
    filteredModules() {
      return this.product.modules.filter((m) => m.category === this.activeCategory)
    },
    demoUrl() {
      const text = encodeURIComponent(this.product.demoText)
      return `https://wa.me/5554992302401?text=${text}`
    },
    moduleDemoUrl() {
      const name = this.selectedModule.name || ''
      const text = encodeURIComponent(
        `${this.product.demoText} — módulo ${name}`
      )
      return `https://wa.me/5554992302401?text=${text}`
    },
  },
  watch: {
    product: {
      immediate: true,
      handler(value) {
        const firstCategory = value.categories && value.categories[0]
        this.activeCategory = firstCategory ? firstCategory.id : ''
      },
    },
  },
  methods: {
    openModal(module) {
      this.selectedModule = module
      this.showModal = true
      document.body.style.overflow = 'hidden'
    },
    closeModal() {
      this.showModal = false
      document.body.style.overflow = ''
    },
    getCategoryName(id) {
      const category = this.product.categories.find((c) => c.id === id)
      return category ? category.name : ''
    },
  },
  beforeUnmount() {
    document.body.style.overflow = ''
  },
}
</script>

<style scoped>
.product-modules-area {
  background: linear-gradient(180deg, #ffffff 0%, #f9f9f9 100%);
}

.categories-tabs {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin: 40px 0 50px;
}

.category-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 30px;
  border: 2px solid #e8e8e8;
  background: white;
  color: #555;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.category-tab i {
  font-size: 18px;
}

.category-tab:hover,
.category-tab.active {
  background: linear-gradient(135deg, #6D5DFC 0%, #4361EE 100%);
  border-color: transparent;
  color: white;
  box-shadow: 0 8px 20px rgba(109, 93, 252, 0.25);
}

.modules-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 24px;
  margin-bottom: 60px;
}

.module-card {
  background: white;
  border: 2px solid #f0f0f0;
  border-radius: 16px;
  padding: 28px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  min-height: 210px;
}

.module-card:hover {
  transform: translateY(-8px);
  border-color: #6D5DFC;
  box-shadow: 0 15px 40px rgba(109, 93, 252, 0.15);
}

.module-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: linear-gradient(135deg, #6D5DFC 0%, #4361EE 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
}

.module-icon i {
  font-size: 26px;
  color: white;
}

.module-card h3 {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 10px;
}

.module-card p {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0;
}

.module-badge {
  position: absolute;
  top: 18px;
  right: 18px;
  background: rgba(109, 93, 252, 0.1);
  color: #6D5DFC;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 20px;
  text-transform: uppercase;
}

.demo-cta {
  background: linear-gradient(135deg, #6D5DFC 0%, #4361EE 100%);
  border-radius: 20px;
  padding: 50px 40px;
  text-align: center;
  box-shadow: 0 15px 50px rgba(109, 93, 252, 0.3);
}

.demo-content h3 {
  color: white;
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 12px;
}

.demo-content p {
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 28px;
  font-size: 16px;
}

.btn-style-one {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  border-radius: 10px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
}

.btn-style-one.green-color {
  background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
  color: white;
}

.btn-style-one.green-color:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(37, 211, 102, 0.45);
  color: white;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  backdrop-filter: blur(5px);
}

.modal-container {
  background: white;
  border-radius: 20px;
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.05);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
}

.modal-header {
  padding: 40px;
  text-align: center;
  background: linear-gradient(135deg, #6D5DFC 0%, #4361EE 100%);
  color: white;
  border-radius: 20px 20px 0 0;
}

.modal-icon {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.modal-icon i {
  font-size: 40px;
  color: white;
}

.modal-header h2 {
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 10px;
  color: white;
}

.modal-category {
  margin: 0;
  opacity: 0.9;
}

.modal-body {
  padding: 40px;
}

.module-description {
  font-size: 16px;
  line-height: 1.7;
  color: #666;
  margin-bottom: 30px;
}

.modal-body h3 {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 20px;
}

.features-list {
  list-style: none;
  padding: 0;
  margin: 0 0 30px;
  display: grid;
  gap: 12px;
}

.features-list li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 15px;
  color: #444;
}

.features-list i {
  font-size: 22px;
  color: #25D366;
  flex-shrink: 0;
}

.modal-footer {
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .modules-grid {
    grid-template-columns: 1fr;
  }

  .demo-cta {
    padding: 40px 24px;
  }

  .demo-content h3 {
    font-size: 22px;
  }

  .modal-header,
  .modal-body {
    padding: 30px 20px;
  }
}
</style>
